import * as z from 'zod';

/* ── Common Primitives ────────────────────────────────────────────────────── */

export const dashboardTabSchema = z.enum([
  'eats',
  'cargo',
  'drive',
  'all',
  'top_couriers',
  'top_vendors',
]);
export type DashboardTab = z.infer<typeof dashboardTabSchema>;

export const dashboardPeriodSchema = z.enum([
  'today',
  'yesterday',
  'week',
  'month',
  'year',
  'custom',
]);
export type DashboardPeriod = z.infer<typeof dashboardPeriodSchema>;

/* ── KPI Metric DTOs ──────────────────────────────────────────────────────── */

export const metricItemSchema = z.object({
  label: z.string(),
  value: z.string(),
  raw: z.number().optional(),
  change_percentage: z.number().optional(),
  trend: z.enum(['up', 'down', 'neutral']).optional(),
});
export type MetricItemDto = z.infer<typeof metricItemSchema>;

export const distanceMetricSchema = z.object({
  label: z.string(),
  value: z.string(),
  avg_km: z.number().optional(),
  max_km: z.number().optional(),
  change_percentage: z.number().optional(),
  trend: z.enum(['up', 'down', 'neutral']).optional(),
});
export type DistanceMetricDto = z.infer<typeof distanceMetricSchema>;

export const dashboardKpisDtoSchema = z.object({
  total_orders: metricItemSchema,
  total_revenue: metricItemSchema,
  new_clients: metricItemSchema,
  new_couriers: metricItemSchema,
  average_check: metricItemSchema,
  average_distance: distanceMetricSchema,
});
export type DashboardKpisDto = z.infer<typeof dashboardKpisDtoSchema>;

/* ── Sales Curve DTOs ─────────────────────────────────────────────────────── */

export const salesPointDtoSchema = z.object({
  date: z.string(),
  label: z.string(),
  value: z.number(),
  orders_count: z.number().optional(),
});
export type SalesPointDto = z.infer<typeof salesPointDtoSchema>;

export const dashboardSalesDtoSchema = z.object({
  title: z.string(),
  total: z.string(),
  period_label: z.string(),
  points: z.array(salesPointDtoSchema),
  y_ticks: z.array(z.number()).default([50000, 40000, 30000, 20000, 10000]),
});
export type DashboardSalesDto = z.infer<typeof dashboardSalesDtoSchema>;

/* ── Courier Status Tracking DTOs ─────────────────────────────────────────── */

export const courierSegmentDtoSchema = z.object({
  id: z.string(),
  label: z.string(),
  count: z.number().optional(),
  percentage: z.number(),
  color: z.string(),
});
export type CourierSegmentDto = z.infer<typeof courierSegmentDtoSchema>;

export const courierTrackingDtoSchema = z.object({
  title: z.string(),
  total_couriers: z.number().optional(),
  segments: z.array(courierSegmentDtoSchema),
});
export type CourierTrackingDto = z.infer<typeof courierTrackingDtoSchema>;

/* ── Top Couriers Leaderboard ─────────────────────────────────────────────── */

export const topCourierItemSchema = z.object({
  rank: z.number(),
  courier_id: z.number(),
  first_name: z.string(),
  last_name: z.string(),
  avatar_url: z.string().nullable().optional(),
  completed_orders: z.number(),
  rating: z.number(),
  total_earned: z.number(),
  on_time_rate: z.number(),
  transport_type: z.string().optional(),
});
export type TopCourierItem = z.infer<typeof topCourierItemSchema>;

export const topCouriersResponseSchema = z.object({
  total: z.number(),
  items: z.array(topCourierItemSchema),
});
export type TopCouriersResponse = z.infer<typeof topCouriersResponseSchema>;

/* ── Top Vendors Leaderboard ──────────────────────────────────────────────── */

export const topVendorItemSchema = z.object({
  rank: z.number(),
  vendor_id: z.number(),
  name: z.string(),
  logo_url: z.string().nullable().optional(),
  category: z.string(),
  completed_orders: z.number(),
  gross_revenue: z.number(),
  cancel_rate: z.number(),
  avg_prep_time_minutes: z.number(),
});
export type TopVendorItem = z.infer<typeof topVendorItemSchema>;

export const topVendorsResponseSchema = z.object({
  total: z.number(),
  items: z.array(topVendorItemSchema),
});
export type TopVendorsResponse = z.infer<typeof topVendorsResponseSchema>;

/* ── Header Global Search & Notifications ─────────────────────────────────── */

export const dashboardSearchResponseSchema = z.object({
  orders: z
    .array(
      z.object({
        id: z.number(),
        display_id: z.string(),
        stage: z.number(),
        client_name: z.string().optional(),
      }),
    )
    .default([]),
  couriers: z
    .array(
      z.object({
        id: z.number(),
        full_name: z.string(),
        phone: z.string(),
      }),
    )
    .default([]),
  merchants: z
    .array(
      z.object({
        id: z.number(),
        name: z.string(),
        branch: z.string().optional(),
      }),
    )
    .default([]),
});
export type DashboardSearchResponse = z.infer<typeof dashboardSearchResponseSchema>;

export const dashboardNotificationItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  message: z.string(),
  type: z.enum(['info', 'warning', 'success', 'error']),
  created_at: z.string(),
  read: z.boolean(),
});
export type DashboardNotificationItem = z.infer<typeof dashboardNotificationItemSchema>;

export const dashboardNotificationsResponseSchema = z.object({
  unread_count: z.number(),
  items: z.array(dashboardNotificationItemSchema),
});
export type DashboardNotificationsResponse = z.infer<
  typeof dashboardNotificationsResponseSchema
>;

/* ── Full Dashboard Envelope with Bidirectional Compatibility ────────────── */

/**
 * Normalizes backend snake_case response into frontend camelCase presentation model.
 */
export function normalizeDashboardDto(raw: unknown) {
  if (!raw || typeof raw !== 'object') return raw;
  const obj = raw as Record<string, unknown>;

  // If already normalized camelCase
  if (
    'kpis' in obj &&
    obj.kpis &&
    'totalOrders' in (obj.kpis as Record<string, unknown>)
  ) {
    return raw;
  }

  const kpis = (obj.kpis ?? {}) as Record<string, Record<string, unknown>>;
  const sales = (obj.sales ?? {}) as Record<string, unknown>;
  const courierTracking = (obj.courier_tracking ?? obj.courierTracking ?? {}) as Record<
    string,
    unknown
  >;

  return {
    dateText: obj.date_text ?? obj.dateText ?? '23 июня 2026',
    tab: obj.tab ?? 'eats',
    kpis: {
      totalOrders: kpis.total_orders ??
        kpis.totalOrders ?? { label: 'Общее количество заказов', value: '0 шт' },
      totalRevenue: kpis.total_revenue ??
        kpis.totalRevenue ?? { label: 'Доход', value: '0 сум' },
      newClients: kpis.new_clients ??
        kpis.newClients ?? { label: 'Новые клиенты', value: '0' },
      newCouriers: kpis.new_couriers ??
        kpis.newCouriers ?? { label: 'Новые курьеры', value: '0' },
      averageCheck: kpis.average_check ??
        kpis.averageCheck ?? { label: 'Средний чек', value: '0 сум' },
      averageDistance: {
        label:
          (kpis.average_distance ?? kpis.averageDistance)?.label ?? 'Средняя дистанция',
        value: (kpis.average_distance ?? kpis.averageDistance)?.value ?? '0 км',
        avgKm:
          (kpis.average_distance ?? kpis.averageDistance)?.avg_km ??
          (kpis.average_distance ?? kpis.averageDistance)?.avgKm,
        maxKm:
          (kpis.average_distance ?? kpis.averageDistance)?.max_km ??
          (kpis.average_distance ?? kpis.averageDistance)?.maxKm,
      },
    },
    sales: {
      title: sales.title ?? 'Все продажи',
      total: sales.total ?? '0 сум',
      periodLabel: sales.period_label ?? sales.periodLabel ?? '',
      points: Array.isArray(sales.points) ? sales.points : [],
      yTicks: sales.y_ticks ?? sales.yTicks ?? [50000, 40000, 30000, 20000, 10000],
    },
    courierTracking: {
      title: courierTracking.title ?? 'Отслеживание курьеров',
      totalCouriers:
        courierTracking.total_couriers ?? courierTracking.totalCouriers ?? 100,
      segments: Array.isArray(courierTracking.segments) ? courierTracking.segments : [],
    },
  };
}
